// A chat room's member usernames as the `memberIds` list (security sprint PR 8a): sorted and
// unique, from `members` strings ("alice, bob") and/or usernames. Since PR 8b chat rows are limited to
// the users in memberIds, so every create writes it.
export function memberIdsOf(...sources) {
  const ids = new Set()
  for (const source of sources) {
    const names = Array.isArray(source) ? source : String(source ?? '').split(',')
    for (const name of names) {
      const id = String(name ?? '').trim()
      if (id) ids.add(id)
    }
  }
  return [...ids].sort()
}

// memberIds for a new message in an existing room: the room's own memberIds plus the sender.
// Since sprint PR 8b the room's ChatRoomUser rows and messages are no evidence of membership:
// anyone signed in can create one naming themselves with memberIds [themselves, a member], and
// taking members from them would write that outsider into every later message's memberIds,
// so they would read the room live. Only a room without memberIds (one the R3 backfill
// missed) falls back to the old derivation from its creator, ChatRoomUser rows and their
// `members` strings, and the loaded messages' senders (people who left keep access, D6).
// Callers pass the getChatRoom result, which selects memberIds (customgraphql/queries.js).
export function roomMemberIds(chatRoom, sender) {
  if (Array.isArray(chatRoom?.memberIds) && chatRoom.memberIds.length > 0) {
    return memberIdsOf(chatRoom.memberIds, [sender])
  }
  const rows = chatRoom?.chatRoomUsers?.items ?? []
  const messages = chatRoom?.messages?.items ?? []
  return memberIdsOf(
    [chatRoom?.createdBy],
    ...rows.map((r) => r?.members),
    rows.map((r) => r?.chatRoomUserUserId),
    messages.map((m) => m?.messageUserId),
    [sender]
  )
}

// Two member lists with the same usernames (order and duplicates aside). An empty or missing
// list never matches: since PR 8b such a row is unreadable anyway.
export function sameMembers(a, b) {
  const x = memberIdsOf(Array.isArray(a) ? a : [])
  return x.length > 0 && x.join(',') === memberIdsOf(Array.isArray(b) ? b : []).join(',')
}

// The membership rows that really belong to `room`: their own memberIds are the room's. A row
// someone else created naming themselves (memberIds [outsider, member]) in the room is left
// out, so the sender's updatedAt write doesn't reach the outsider's onUpdateChatRoomUser.
export function rowsOfRoom(rows, room) {
  return (rows ?? []).filter((r) => sameMembers(r?.memberIds, room?.memberIds))
}
